import { useCallback, useRef, useState } from "react";
import type { MouseEvent } from "react";

type DragHandlers = {
  onMouseDown: (e: MouseEvent<HTMLElement>) => void;
  onMouseMove: (e: MouseEvent<HTMLElement>) => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
};

export function useDragScroll<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const drag = useRef({
    active: false,
    startX: 0,
    startScrollLeft: 0,
  });

  const onMouseDown = useCallback((e: MouseEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    const el = ref.current;
    if (!el) return;
    drag.current.active = true;
    drag.current.startX = e.pageX;
    drag.current.startScrollLeft = el.scrollLeft;
    setIsDragging(true);
  }, []);

  const onMouseMove = useCallback((e: MouseEvent<HTMLElement>) => {
    const el = ref.current;
    if (!el || !drag.current.active) return;
    const deltaX = e.pageX - drag.current.startX;
    el.scrollLeft = drag.current.startScrollLeft - deltaX;
    e.preventDefault();
  }, []);

  const stopDrag = useCallback(() => {
    drag.current.active = false;
    setIsDragging(false);
  }, []);

  const dragHandlers: DragHandlers = {
    onMouseDown,
    onMouseMove,
    onMouseUp: stopDrag,
    onMouseLeave: stopDrag,
  };

  return { ref, isDragging, dragHandlers };
}
